import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  IconButton,
  Typography,
  Button,
  Container,
  Link,
  useTheme,
  MobileStepper,
} from '@mui/material';
import SwipeableViews from 'react-swipeable-views';
import { ArrowBack } from '@mui/icons-material';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

const ProductPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const product = location.state?.product;

  const [activeStep, setActiveStep] = useState(0);
  const images = product?.media?.images || [];
  const maxSteps = images.length;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepChange = (step) => {
    setActiveStep(step);
  };

  if (!product) {
    return (
      <Box sx={{ mt: 10, px: 3, maxWidth: 600, mx: 'auto' }}>
        <Typography>Product not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 10, px: 3, maxWidth: 600, mx: 'auto', pb: 10 }}>
      {/* Image Carousel */}
      <Box sx={{ mt: 7, width: '100%', position: 'relative' }}>
        <SwipeableViews
          axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
          index={activeStep}
          onChangeIndex={handleStepChange}
          enableMouseEvents
          resistance
          springConfig={{
            duration: '0.3s',
            easeFunction: 'cubic-bezier(0.15, 0.3, 0.25, 1)',
            delay: '0s'
          }}
        >
          {images.map((image, index) => (
            <div key={index}>
              {Math.abs(activeStep - index) <= 2 ? (
                <Box
                  component="img"
                  sx={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    maxHeight: '50vh',
                    objectFit: 'cover',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                    pointerEvents: 'none',
                    '-webkit-user-drag': 'none',
                    borderRadius: '24px'
                  }}
                  src={image.url}
                  alt={image.alternateText || `Product image ${index + 1}`}
                  draggable="false"
                />
              ) : null}
            </div>
          ))}
        </SwipeableViews>

        {/* Navigation Arrows */}
        <IconButton
          size="small"
          onClick={handleNext}
          disabled={activeStep === maxSteps - 1}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
            },
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1
          }}
        >
          <KeyboardArrowRight />
        </IconButton>

        <IconButton
          size="small"
          onClick={handleBack}
          disabled={activeStep === 0}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
            },
            position: 'absolute',
            left: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1
          }}
        >
          <KeyboardArrowLeft />
        </IconButton>

        {/* Image Stepper (Dots) */}
        <MobileStepper
          steps={maxSteps}
          position="static"
          activeStep={activeStep}
          sx={{ 
            bgcolor: theme.palette.grey[100],
            position: 'absolute',
            bottom: 30,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'auto',
            // minWidth: '120px',
            borderRadius: '20px',
            padding: '6px 8px',
            justifyContent: 'center',
            zIndex: 1,
            '& .MuiMobileStepper-dots': {
              margin: '0 auto'
            },
            '& .MuiMobileStepper-dot': {
              backgroundColor: theme.palette.grey[300],
              margin: '0 4px',
              width: 6,
              height: 6,
              '&.MuiMobileStepper-dotActive': {
                backgroundColor: theme.palette.grey[600],
              }
            },
          }}
          nextButton={<div />}
          backButton={<div />}
        />
      </Box>

      {/* Product Info */}
      <Box sx={{ mt: 2 }}>
        {/* Brand */}
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 600,
            color: theme.palette.primary.main 
          }}
        >
          {product.brand?.name || 'Brand'}
        </Typography>

        {/* Product Name */}
        <Typography 
          variant="h6" 
          sx={{ 
            mt: 1,
            fontWeight: 500
          }}
        >
          {product.title}
        </Typography>

        {/* Price */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            £{product.price}
          </Typography>
          {product.product_details?.original_price && (
            <Typography 
              variant="body1" 
              sx={{ 
                textDecoration: 'line-through',
                color: theme.palette.text.secondary
              }}
            >
              £{product.product_details.original_price}
            </Typography>
          )}
        </Box>

        {/* Sizes */}
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Available Sizes
            </Typography>
            {product.product_details?.sizeGuideUrl && (
              <Link
                href={product.product_details.sizeGuideUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Size Guide
              </Link>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {product.variants?.map((variant, index) => (
              <Button
                key={index}
                variant="contained"
                sx={{
                  minWidth: '60px',
                  height: '40px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: theme.palette.grey[100],
                  color: theme.palette.text.primary,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: theme.palette.grey[200],
                    boxShadow: 'none',
                  },
                  ...(variant.isInStock ? {} : {
                    opacity: 0.5,
                    textDecoration: 'line-through',
                  }),
                }}
                disabled={!variant.isInStock}
              >
                {variant.size}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductPage; 